import { useState } from 'react';
import { Filter, Plus, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { MemberAvatar } from '@/components/MemberAvatar';
import { useCommandPalette } from '@/components/CommandPaletteProvider';
import { cn } from '@/lib/utils';

/**
 * Glass redesign — prototype-faithful right-side topbar controls.
 *
 * Renders these in order, matching `renderTopBarControls`:
 *   1. Search button ("Search" + ⌘K)              — opens the
 *      workspace-scoped command palette
 *   2. Filter button + popover (assignees, tags)  — `.v1-toolbar-btn` + `.gp-popover`
 *   3. Overlapping avatar stack (top 4 + "+N")    — clickable; toggles
 *      that member in the assignee filter
 *
 * The "+ New task" CTA is rendered separately via `<NewTaskCtaButton>`
 * because it sits AFTER the theme toggle in the prototype's order
 * (Search · Filter · Avatars · ViewSwitcher · Theme · NewTask).
 * AppTopBar provides a dedicated `cta` slot for that.
 *
 * Search is wired through `useCommandPalette()` to the workspace-
 * scoped palette mounted by `WorkspaceShell`. ⌘K / Ctrl+K is the
 * global keyboard shortcut for the same palette and works on every
 * workspace-scoped route (not just the project pages this button
 * appears on).
 *
 * Filter is wired to local state passed in from BoardPage; toggling
 * an assignee or tag flips inclusion in the corresponding array,
 * "Clear all" wipes both, the active count badge mirrors the
 * `--brand-accent` orange circle from `.gp-filter-active`.
 *
 * The avatar stack uses the workspace members list — first 4 by
 * the order the API returns; "+N" appears when there are more.
 * Each avatar is a one-click shortcut into `filter.assigneeIds`:
 * click to add the member, click again to remove. Active members
 * get a brand-accent ring so the topbar reflects current filter
 * state without forcing the user to open the popover.
 *
 * Note: no responsive `xl:` hides anywhere — the prototype renders
 * everything at every viewport. If the bar doesn't fit, the OS
 * horizontal scrolling kicks in (none does in the prototype because
 * the icon ViewSwitcher already shrinks the right cluster aggressively).
 */
export interface BoardFilter {
  assigneeIds: string[];
  tagIds: string[];
}

export interface BoardTopBarActionsProps {
  filter: BoardFilter;
  setFilter: (next: BoardFilter) => void;
  members: ReadonlyArray<{
    id: string;
    name: string | null;
    email: string | null;
  }>;
  tags: ReadonlyArray<{ id: string; name: string; color: string }>;
}

export const BoardTopBarActions = ({
  filter,
  setFilter,
  members,
  tags,
}: BoardTopBarActionsProps) => {
  const filterCount = filter.assigneeIds.length + filter.tagIds.length;
  const visibleAvatars = members.slice(0, 4);
  const extraCount = Math.max(0, members.length - visibleAvatars.length);
  const palette = useCommandPalette();

  const togglePerson = (id: string): void => {
    const set = new Set(filter.assigneeIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    setFilter({ ...filter, assigneeIds: [...set] });
  };

  return (
    <>
      {/* ── Search ── opens the workspace command palette */}
      <button
        type="button"
        onClick={() => palette.open()}
        className={cn(toolbarBtnClasses)}
        aria-label="Open command palette (⌘K)"
      >
        <Search className="size-3.5" aria-hidden="true" />
        <span>Search</span>
        <kbd
          className={cn(
            'border-border bg-secondary text-foreground-faint inline-flex h-[18px] items-center rounded-[4px] border px-1 font-mono text-[10px]',
          )}
        >
          ⌘K
        </kbd>
      </button>

      {/* ── Filter ── */}
      <FilterPopover
        filter={filter}
        setFilter={setFilter}
        members={members}
        tags={tags}
        filterCount={filterCount}
      />

      {/* ── Avatar stack ── (workspace members, top 4 + "+N").
          Clicking an avatar toggles that member in the assignee
          filter — same effect as opening the Filter popover and
          clicking their row, but in one click. The brand-accent
          ring on active avatars surfaces filter state without
          forcing the user to open the popover. */}
      {visibleAvatars.length > 0 ? (
        <div className="flex items-center">
          {visibleAvatars.map((m, i) => {
            const active = filter.assigneeIds.includes(m.id);
            const displayName = m.name ?? m.email ?? 'Unknown';
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => togglePerson(m.id)}
                aria-pressed={active}
                aria-label={
                  active
                    ? `Remove ${displayName} from assignee filter`
                    : `Filter to ${displayName}`
                }
                title={
                  active
                    ? `Filtering to ${displayName} — click to remove`
                    : `Filter to ${displayName}`
                }
                className={cn(
                  'rounded-full border-0 bg-transparent p-0 transition-transform duration-100 hover:-translate-y-px focus-visible:outline-none',
                  i > 0 && '-ml-1.5',
                )}
              >
                <MemberAvatar
                  id={m.id}
                  name={m.name}
                  email={m.email}
                  size="xs"
                  className={cn(
                    '!h-[22px] !w-[22px] !text-[9px]',
                    active
                      ? 'shadow-[0_0_0_2px_var(--background),0_0_0_3.5px_var(--brand-accent)]'
                      : 'shadow-[0_0_0_2px_var(--background)]',
                  )}
                />
              </button>
            );
          })}
          {extraCount > 0 ? (
            <span className="text-foreground-muted ml-1.5 text-[10.5px]">+{extraCount}</span>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

/**
 * "+ New task" navy CTA. Rendered separately so it can live in the
 * topbar's `cta` slot (after the theme toggle) per prototype order.
 *
 * Children optional override of the label — defaults to "New task".
 */
export const NewTaskCtaButton = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children?: ReactNode;
}) => (
  <button type="button" onClick={onClick} className={cn(ctaClasses)}>
    <Plus className="size-3.5" aria-hidden="true" />
    {children ?? 'New task'}
  </button>
);

// ── Tailwind class strings extracted for re-use ──────────────────────

// `.v1-toolbar-btn` — translucent glass button used by Search + Filter.
const toolbarBtnClasses = cn(
  'inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[12.5px] transition-all duration-100',
  'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] backdrop-blur-md',
  'text-foreground-muted hover:text-foreground hover:border-foreground-faint',
);

// `.v1-cta` — navy New Task button. Brand shadow.
const ctaClasses = cn(
  'inline-flex h-7 items-center gap-1.5 rounded-lg px-2.5 text-[12.5px] font-medium text-white transition-shadow duration-100',
  'bg-[var(--primary)]',
  'shadow-[0_2px_8px_oklch(0.3_0.06_245/0.3)] hover:shadow-[0_3px_10px_oklch(0.3_0.06_245/0.4)]',
  'dark:shadow-[0_2px_12px_oklch(0.55_0.12_245/0.4)]',
);

// ── Filter popover ───────────────────────────────────────────────────

interface FilterPopoverProps {
  filter: BoardFilter;
  setFilter: (next: BoardFilter) => void;
  members: BoardTopBarActionsProps['members'];
  tags: BoardTopBarActionsProps['tags'];
  filterCount: number;
}

const FilterPopover = ({ filter, setFilter, members, tags, filterCount }: FilterPopoverProps) => {
  // Controlled open so we can keep the "is-on" feedback responsive
  // without a remount on every selection change.
  const [open, setOpen] = useState(false);

  const togglePerson = (id: string): void => {
    const set = new Set(filter.assigneeIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    setFilter({ ...filter, assigneeIds: [...set] });
  };

  const toggleTag = (id: string): void => {
    const set = new Set(filter.tagIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    setFilter({ ...filter, tagIds: [...set] });
  };

  const clearAll = (): void => setFilter({ assigneeIds: [], tagIds: [] });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(toolbarBtnClasses)}>
          <Filter className="size-3.5" aria-hidden="true" />
          Filter
          {filterCount > 0 ? (
            // `.gp-filter-active` — orange circle, white text, 16px.
            <span
              aria-label={`${filterCount} active`}
              className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[var(--brand-accent)] text-[10px] font-semibold text-white"
            >
              {filterCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className={cn(
          // `.gp-popover` — flat (NOT glass), opaque bg, soft shadow.
          'bg-background text-foreground min-w-[220px] rounded-[10px] border p-1.5',
          'border-[var(--topbar-border,var(--border))]',
          'shadow-[0_12px_32px_oklch(0_0_0/0.18)] ring-0',
        )}
      >
        <SectionLabel>Assignee</SectionLabel>
        {members.length === 0 ? (
          <EmptyRow>No members</EmptyRow>
        ) : (
          members.map((m) => {
            const on = filter.assigneeIds.includes(m.id);
            return (
              <PopoverRow key={m.id} active={on} onClick={() => togglePerson(m.id)}>
                <CheckSlot active={on} />
                <MemberAvatar
                  id={m.id}
                  name={m.name}
                  email={m.email}
                  size="xs"
                  className="!h-[18px] !w-[18px] !text-[8px]"
                />
                <span className="truncate">{m.name ?? m.email ?? 'Unknown'}</span>
              </PopoverRow>
            );
          })
        )}

        <SectionLabel className="mt-1">Tag</SectionLabel>
        {tags.length === 0 ? (
          <EmptyRow>No tags</EmptyRow>
        ) : (
          tags.map((t) => {
            const on = filter.tagIds.includes(t.id);
            return (
              <PopoverRow key={t.id} active={on} onClick={() => toggleTag(t.id)}>
                <CheckSlot active={on} />
                <span
                  aria-hidden="true"
                  className="h-[7px] w-[7px] shrink-0 rounded-full"
                  style={{ background: t.color }}
                />
                <span className="truncate">{t.name}</span>
              </PopoverRow>
            );
          })
        )}

        {filterCount > 0 ? (
          <button
            type="button"
            onClick={clearAll}
            className={cn(
              'mt-1 inline-flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] font-medium',
              'text-[var(--brand-accent)] hover:bg-[oklch(0.74_0.16_50/0.10)]',
            )}
          >
            <span className="inline-flex w-3.5" />
            Clear all
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

// ── Popover sub-bits ─────────────────────────────────────────────────

const SectionLabel = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div
    className={cn(
      'text-foreground-faint px-2 pt-1.5 pb-1 text-[10.5px] font-semibold tracking-[0.06em] uppercase',
      className,
    )}
  >
    {children}
  </div>
);

const PopoverRow = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'text-foreground hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px]',
      active && 'font-medium',
    )}
  >
    {children}
  </button>
);

const CheckSlot = ({ active }: { active: boolean }) => (
  <span aria-hidden="true" className="text-foreground-muted inline-flex w-3.5 justify-center text-[12px]">
    {active ? '✓' : ''}
  </span>
);

const EmptyRow = ({ children }: { children: React.ReactNode }) => (
  <div className="text-foreground-faint px-2 py-1.5 text-[12.5px] italic">{children}</div>
);
